import React, { createContext, useContext } from 'react';

/**
 * ArticleNav の描画スロット・コンテキストを共有する Context。
 *
 * ArticleNav は knz 本文中の 1 箇所に書かれるが、描画は記事の上部と下部に振り分ける（仕様 Q-1 案X）。
 * KnzViewer が本文の先頭で slot='top'、末尾で slot='bottom' のプロバイダで ArticleNav ノードを
 * 2 回描画し、ArticleNav はこのコンテキストの slot を見て「前後ナビのみ（top）」か
 * 「前後ナビ＋関連記事（bottom）」かを出し分ける。
 *
 * navContext は遷移方法（Q-8）:
 * - 'preview'（ワークスペースのプレビュー）: リンクは新規タブで /devdocs/article/:id を開く
 * - 'article'（記事詳細 ArticleDetail）: 通常のルーティング遷移
 *
 * reportBottomHeight は、下部スロット（slot='bottom'）の描画領域の実高さ（px）を親へ通知する
 * コールバック。記事詳細（ArticleDetail）が末尾スペーサーから ArticleNav の領域ぶんを差し引くために使う。
 */
export type ArticleNavSlot = 'top' | 'bottom';
export type ArticleNavContextValue = 'preview' | 'article';

type ArticleNavContextType = {
  slot: ArticleNavSlot;
  navContext: ArticleNavContextValue;
  /** 下部スロットの実高さ（px）を親へ通知する（slot='bottom' のときのみ意味を持つ） */
  reportBottomHeight?: (height: number) => void;
};

const ArticleNavContext = createContext<ArticleNavContextType | null>(null);

/**
 * ArticleNav のスロット・遷移コンテキストを取得する。
 * プロバイダ外（＝KnzViewer が上下振り分けをしていない文脈）では null を返し、
 * ArticleNav 側は描画しない（本文中にそのまま出さない）。
 */
export const useArticleNavContext = (): ArticleNavContextType | null => {
  return useContext(ArticleNavContext);
};

export const ArticleNavSlotProvider: React.FC<{
  slot: ArticleNavSlot;
  navContext: ArticleNavContextValue;
  reportBottomHeight?: (height: number) => void;
  children: React.ReactNode;
}> = ({ slot, navContext, reportBottomHeight, children }) => {
  const value = React.useMemo(
    () => ({ slot, navContext, reportBottomHeight }),
    [slot, navContext, reportBottomHeight],
  );
  return <ArticleNavContext.Provider value={value}>{children}</ArticleNavContext.Provider>;
};
